import { useEffect, useState } from 'react';
import { fetchQuickReplies, type QuickReplyTemplate } from '@/lib/api';

interface QuickReplySheetProps {
  open: boolean;
  slug: string;
  onClose: () => void;
  /** Caller drops the picked template into the ReplyBar. */
  onPick: (body: string) => void;
}

/**
 * Bottom-sheet picker for owner-defined quick reply templates. Pulls the
 * latest list from the Worker on open so edits done on another device flow
 * in without a hard refresh.
 *
 * SMS compliance is enforced at the Worker level (templates are stored with
 * the canonical `Reply STOP to opt out.` trailer baked in), so the picker
 * just renders what the server returns. If the Worker hasn't deployed the
 * polish endpoints yet the helper returns an empty list and we render an
 * inline "no templates yet" empty state instead of crashing.
 */
export function QuickReplySheet({ open, slug, onClose, onPick }: QuickReplySheetProps) {
  const [templates, setTemplates] = useState<QuickReplyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchQuickReplies(slug)
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load templates');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug]);

  if (!open) return null;

  return (
    <div
      // z-[60] sits above the ReplyBar (position:fixed, zIndex:50) — same
      // occlusion fix as CallContextBar (Item 2, 2026-06-05). At z-40 a long
      // template list's bottom rows were painted over by the reply bar.
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Quick reply templates"
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white dark:bg-[#0F172A] pb-6 shadow-ss-md animate-ss-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <div className="flex justify-center pt-2">
          <span className="h-1.5 w-12 rounded-full bg-ink-200 dark:bg-[#334155]" />
        </div>
        <div className="flex items-center justify-between px-5 pb-2 pt-3">
          <div className="text-[15px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
            Quick replies
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-ink-500 dark:text-[#94A3B8]"
          >
            Close
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-3">
          {loading ? (
            <div className="px-2 py-6 text-center text-sm text-ink-500 dark:text-[#94A3B8]">
              Loading…
            </div>
          ) : error ? (
            <div className="px-2 py-6 text-center text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : templates.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-ink-500 dark:text-[#94A3B8]">
              No quick replies yet. Add some in Settings → Quick Replies.
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5 pb-2">
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(t.body);
                      onClose();
                    }}
                    className="ss-press w-full rounded-xl border border-ink-100 dark:border-[#334155] bg-ink-50 dark:bg-[#1E293B] px-3 py-2.5 text-left"
                  >
                    <div className="text-[13px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
                      {t.label}
                    </div>
                    <div className="mt-0.5 text-xs leading-snug text-ink-500 dark:text-[#94A3B8]">
                      {t.body}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
